import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { MeOutput } from "@cx/contracts";
import { ApiFailure, apiFetch } from "./api";
import { clearPdvCache } from "./pdv/store";

type Api = <T>(path: string, init?: RequestInit) => Promise<T>;
type Session = {
  me: MeOutput;
  companyId: string;
  setCompanyId(id: string): void;
  logout(): Promise<void>;
  api: Api;
};
const Ctx = createContext<Session | null>(null);
const COMPANY_KEY = "cx.company";
const readCompany = () => {
  try {
    return localStorage.getItem(COMPANY_KEY);
  } catch {
    return null;
  }
};

/**
 * Convenção de cache: toda query de dados usa a chave [recurso, companyId, ...]. Trocar de empresa só remove as chaves
 * da empresa anterior; as novas chaves buscam sozinhas, já com o header certo (FE9).
 */
export function SessionProvider({ children, fallback }: { children: ReactNode; fallback: ReactNode }) {
  const qc = useQueryClient();
  const me = useQuery<MeOutput | null>({
    queryKey: ["me"],
    queryFn: () => apiFetch<MeOutput>("/me"),
    retry: false,
    staleTime: 60_000,
  });
  const [stored, setStored] = useState<string | null>(readCompany);
  useEffect(() => {
    const onUnauth = () => {
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== "me" && q.queryKey[0] !== "config" });
      qc.setQueryData(["me"], null);
    };
    window.addEventListener("cx:unauthenticated", onUnauth);
    return () => window.removeEventListener("cx:unauthenticated", onUnauth);
  }, [qc]);

  const companies = me.data?.companies ?? [];
  const companyId = companies.some((c) => c.id === stored) ? stored : (companies[0]?.id ?? null);
  const api = useCallback<Api>((path, init = {}) => apiFetch(path, { ...init, companyId }), [companyId]);
  const setCompanyId = useCallback(
    (id: string) => {
      if (id === companyId) return;
      try {
        localStorage.setItem(COMPANY_KEY, id);
      } catch {
        /* sem storage: vale só nesta aba */
      }
      setStored(id);
      if (companyId) qc.removeQueries({ predicate: (q) => q.queryKey[1] === companyId });
    },
    [companyId, qc],
  );
  const logout = useCallback(async () => {
    try {
      await apiFetch("/auth/logout", { method: "POST" });
    } finally {
      qc.clear();
      // PDV (revisão de segurança T3.1): a sessão e o catálogo guardados para o modo offline saem com o usuário.
      await clearPdvCache();
      window.location.assign("/");
    }
  }, [qc]);
  const value = useMemo<Session | null>(
    () => (me.data && companyId ? { me: me.data, companyId, setCompanyId, logout, api } : null),
    [me.data, companyId, setCompanyId, logout, api],
  );

  if (me.isLoading)
    return (
      <div className="p-5 text-center" role="status">
        Carregando…
      </div>
    );
  if (me.isError && !(me.error instanceof ApiFailure && me.error.status === 401)) {
    return (
      <div className="p-5 text-center">
        <div className="alert alert-danger d-inline-block" role="alert">
          Não foi possível carregar a sessão.
        </div>
        <div>
          <button type="button" className="btn btn-outline-secondary" onClick={() => me.refetch()}>
            Tentar de novo
          </button>
        </div>
      </div>
    );
  }
  if (!me.data) return <>{fallback}</>;
  if (!value) return <NoCompany onLogout={logout} />;
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

function NoCompany({ onLogout }: { onLogout(): void }) {
  return (
    <main className="min-vh-100 d-flex align-items-center justify-content-center p-3">
      <div className="card p-4 text-center" style={{ maxWidth: 420 }}>
        <h1 className="h5">Sem empresa liberada</h1>
        <p className="text-muted">
          Seu usuário não tem acesso a nenhuma empresa. Peça ao administrador para liberar o acesso.
        </p>
        <button type="button" className="btn btn-outline-secondary" onClick={onLogout}>
          Sair
        </button>
      </div>
    </main>
  );
}

export function useSession() {
  const s = useContext(Ctx);
  if (!s) throw new Error("useSession fora do SessionProvider");
  return s;
}
export const useApi = () => useSession().api;
